const apiUrl = import.meta.env.VITE_API_URL ?? "http://localhost:4000";

export type AdminUser = Readonly<{
  userId: string;
  email: string;
  displayName: string;
  role:
    "OWNER" | "ADMIN" | "SALES" | "PM" | "DEVELOPER" | "SUPPORT" | "FINANCE";
  permissions: readonly string[];
  sessionId: string;
  csrfToken: string;
}>;

export type AdminOverview = Readonly<{
  counts: Readonly<{
    paidUnfulfilled: number;
    overdueProjects: number;
    unansweredTickets: number;
    renewalsDue: number;
  }>;
  queue: readonly Readonly<{
    id: string;
    type: string;
    title: string;
    status: string;
    age: string;
    assignee?: string;
  }>[];
  incidents: readonly Readonly<{
    id: string;
    severity: string;
    title: string;
    createdAt: string;
  }>[];
  backup: Readonly<{ status: string; lastSuccessAt: string | null }>;
}>;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${apiUrl}/api/v1${path}`, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      message?: string;
      error?: { message?: string };
    } | null;
    throw new ApiError(
      response.status,
      payload?.error?.message ??
        payload?.message ??
        `Request failed (${response.status})`,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export type PageMeta = Readonly<{
  total: number;
  page: number;
  pageSize: number;
  pages: number;
}>;
export type Paginated<T> = Readonly<{ data: readonly T[]; meta: PageMeta }>;

export type IntegrationConfiguration = Readonly<{
  id: string;
  values: Readonly<Record<string, string | number>>;
}>;

export type TransactionalEmailPreview = Readonly<{
  id: string;
  name: string;
  description: string;
  subject: string;
  html: string;
}>;

export async function getTransactionalEmailPreviews() {
  const payload = await request<{
    data: readonly TransactionalEmailPreview[];
  }>("/admin/email-templates");
  return payload.data;
}

export async function getIntegrationConfiguration(id: string) {
  const payload = await request<{ data: IntegrationConfiguration }>(
    `/admin/integrations/${encodeURIComponent(id)}/configuration`,
  );
  return payload.data;
}

export async function sendIntegrationTestEmail(
  admin: AdminUser,
  id: string,
  email: string,
) {
  return request<{ data: { sent: true; checkedAt: string } }>(
    `/admin/integrations/${encodeURIComponent(id)}/test-email`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": admin.csrfToken },
      body: JSON.stringify({ email }),
    },
  );
}

export type AdminPackage = Readonly<{
  id: string;
  slug: string;
  name: string;
  eyebrow: string;
  description: string;
  websiteType: string;
  pageLimit: number;
  sectionLimit: number | null;
  deliveryBusinessDays: number;
  annualRevisions: number;
  firstResponseHours: number;
  active: boolean;
  featured: boolean;
  sortOrder: number;
  features: readonly Readonly<{ title: string; description: string }>[];
  version: number;
  prices: readonly Readonly<{
    id: string;
    currency: string;
    amountMinor: string;
    renewal: boolean;
  }>[];
}>;
export type AdminAddOn = Readonly<{
  id: string;
  slug: string;
  name: string;
  description: string;
  recurring: boolean;
  active: boolean;
  rules: Record<string, unknown>;
  version: number;
  prices: readonly Readonly<{
    id: string;
    currency: string;
    amountMinor: string;
    renewal: boolean;
  }>[];
}>;
export type AdminCustomer = Readonly<{
  id: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  status: string;
  country: string;
  orderCount: number;
  projectCount: number;
  lifetimeValueMinor: string;
  createdAt: string;
}>;
export type AdminOrder = Readonly<{
  id: string;
  number: string;
  status: string;
  currency: string;
  totalMinor: string;
  version: number;
  customer: string;
  package: string;
  createdAt: string;
  payment: Readonly<{
    id: string;
    provider: string;
    mode: string;
    status: string;
  }> | null;
}>;
export type AdminPayment = Readonly<{
  id: string;
  provider: string;
  mode: string;
  status: string;
  amountMinor: string;
  currency: string;
  providerReference: string | null;
  evidence: unknown;
  createdAt: string;
  order: Readonly<{
    id: string;
    number: string;
    version: number;
    status: string;
  }> | null;
  customer: Readonly<{ id: string; displayName: string }> | null;
}>;
export type AdminProject = Readonly<{
  id: string;
  name: string;
  status: string;
  websiteUrl: string | null;
  assignedPmId: string | null;
  assignedDevId: string | null;
  deliveryDueAt: string | null;
  version: number;
  customer: string;
  package: string;
  pm: string | null;
  developer: string | null;
  updatedAt: string;
}>;
export type AdminAssignee = Readonly<{
  id: string;
  email: string | null;
  displayName: string;
  roles: readonly AdminUser["role"][];
}>;

function queryString(input: {
  q?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}) {
  const search = new URLSearchParams();
  if (input.q) search.set("q", input.q);
  if (input.status) search.set("status", input.status);
  if (input.page) search.set("page", String(input.page));
  if (input.pageSize) search.set("pageSize", String(input.pageSize));
  return search.size ? `?${search}` : "";
}

export function getPackages(input: { q?: string; page?: number } = {}) {
  return request<Paginated<AdminPackage>>(
    `/admin/packages${queryString(input)}`,
  );
}
export function getAddOns(input: { q?: string; page?: number } = {}) {
  return request<Paginated<AdminAddOn>>(`/admin/add-ons${queryString(input)}`);
}
export function getCustomers(input: { q?: string; page?: number } = {}) {
  return request<Paginated<AdminCustomer>>(
    `/admin/customers${queryString(input)}`,
  );
}
export function getOrders(
  input: { q?: string; status?: string; page?: number } = {},
) {
  return request<Paginated<AdminOrder>>(`/admin/orders${queryString(input)}`);
}
export function getPayments(
  input: { q?: string; status?: string; page?: number } = {},
) {
  return request<Paginated<AdminPayment>>(
    `/admin/payments${queryString(input)}`,
  );
}
export function getProjects(
  input: { q?: string; status?: string; page?: number } = {},
) {
  return request<Paginated<AdminProject>>(
    `/admin/projects${queryString(input)}`,
  );
}
export function getProjectAssignees() {
  return request<{ data: readonly AdminAssignee[] }>(
    "/admin/project-assignees",
  );
}
export function getOperationalAssignees(kind: "ticket" | "sales") {
  return request<{ data: readonly AdminAssignee[] }>(
    `/admin/${kind}-assignees`,
  );
}

export async function reviewOfflinePayment(
  admin: AdminUser,
  paymentId: string,
  input: {
    decision: "APPROVE" | "REJECT";
    orderVersion: number;
    externalReference?: string;
    note?: string;
  },
) {
  return request<{
    data: {
      paymentId: string;
      paymentStatus: string;
      orderId: string;
      orderStatus: string;
      replayed: boolean;
    };
  }>(`/admin/payments/${paymentId}/review`, {
    method: "POST",
    headers: { "X-CSRF-Token": admin.csrfToken },
    body: JSON.stringify(input),
  });
}

export async function uploadAdminMedia(
  admin: AdminUser,
  file: File,
  kind: "BLOG_FEATURED_IMAGE",
) {
  const contentType = file.type as "image/png" | "image/jpeg" | "image/webp";
  if (!["image/png", "image/jpeg", "image/webp"].includes(contentType))
    throw new Error("Use a PNG, JPEG or WebP image.");
  if (file.size > 800_000) throw new Error("Image must be 800 KB or smaller.");
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(new Error("Could not read the selected image."));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsDataURL(file);
  });
  const dataBase64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  return request<{
    data: { key: string; url: string; contentType: string; sizeBytes: number };
  }>("/admin/media", {
    method: "POST",
    headers: { "X-CSRF-Token": admin.csrfToken },
    body: JSON.stringify({ kind, contentType, dataBase64 }),
  });
}

export async function updatePackage(
  admin: AdminUser,
  packageId: string,
  input: {
    version: number;
    active?: boolean;
    name?: string;
    eyebrow?: string;
    description?: string;
    websiteType?: string;
    pageLimit?: number;
    sectionLimit?: number;
    deliveryBusinessDays?: number;
    annualRevisions?: number;
    firstResponseHours?: number;
    featured?: boolean;
    sortOrder?: number;
    features?: readonly Readonly<{ title: string; description: string }>[];
  },
) {
  return request<{ data: AdminPackage }>(`/admin/packages/${packageId}`, {
    method: "PATCH",
    headers: { "X-CSRF-Token": admin.csrfToken },
    body: JSON.stringify(input),
  });
}

export async function saveAddOn(
  admin: AdminUser,
  addOnId: string | null,
  input: {
    slug?: string;
    name: string;
    description: string;
    recurring: boolean;
    active: boolean;
    rules: Record<string, unknown>;
    version?: number;
  },
) {
  return request<{ data: AdminAddOn }>(
    addOnId ? `/admin/add-ons/${addOnId}` : "/admin/add-ons",
    {
      method: addOnId ? "PATCH" : "POST",
      headers: { "X-CSRF-Token": admin.csrfToken },
      body: JSON.stringify(input),
    },
  );
}

export async function saveCatalogPrice(
  admin: AdminUser,
  kind: "packages" | "add-ons",
  id: string,
  input: { currency: string; amountMinor: string; renewal: boolean },
) {
  return request<{
    data: {
      id: string;
      currency: string;
      amountMinor: string;
      renewal: boolean;
    };
  }>(`/admin/${kind}/${id}/prices`, {
    method: "POST",
    headers: { "X-CSRF-Token": admin.csrfToken },
    body: JSON.stringify(input),
  });
}

export async function updateProject(
  admin: AdminUser,
  projectId: string,
  input: {
    version: number;
    status?: string;
    websiteUrl?: string;
    assignedPmId?: string | null;
    assignedDevId?: string | null;
  },
) {
  return request<{ data: AdminProject }>(`/admin/projects/${projectId}`, {
    method: "PATCH",
    headers: { "X-CSRF-Token": admin.csrfToken },
    body: JSON.stringify(input),
  });
}

export async function updateOrderStatus(
  admin: AdminUser,
  orderId: string,
  input: { version: number; status: string; reason?: string },
) {
  return request<{ data: AdminOrder }>(`/admin/orders/${orderId}/status`, {
    method: "PATCH",
    headers: { "X-CSRF-Token": admin.csrfToken },
    body: JSON.stringify(input),
  });
}

export type AdminRecord = Readonly<Record<string, unknown> & { id: string }>;

export function getAdminList<T extends AdminRecord = AdminRecord>(
  resource: string,
  input: { q?: string; status?: string; page?: number; pageSize?: number } = {},
) {
  return request<Paginated<T>>(`/admin/${resource}${queryString(input)}`);
}

export function adminMutation<T>(
  admin: AdminUser,
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  input: unknown,
) {
  return request<T>(`/admin/${path}`, {
    method,
    headers: { "X-CSRF-Token": admin.csrfToken },
    body: JSON.stringify(input),
  });
}

export function getPlatformSettings() {
  return request<{
    data: Readonly<
      Record<
        string,
        Readonly<{
          value: Record<string, unknown>;
          version: number;
          updatedAt: string;
        }>
      >
    >;
  }>("/admin/settings");
}

export async function loginAdmin(email: string, password: string) {
  const payload = await request<{ data: AdminUser }>("/admin/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  return payload.data;
}

export async function getCurrentAdmin(signal?: AbortSignal) {
  try {
    const payload = await request<{ data: AdminUser }>("/admin/auth/me", {
      signal: signal ?? null,
    });
    return payload.data;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export async function logoutAdmin(admin: AdminUser) {
  await request<void>("/admin/auth/logout", {
    method: "POST",
    headers: { "X-CSRF-Token": admin.csrfToken },
  });
}

export async function getOverview(
  signal?: AbortSignal,
): Promise<AdminOverview> {
  const payload = await request<{ data: AdminOverview }>("/admin/overview", {
    signal: signal ?? null,
  });
  return payload.data;
}
